import { faker } from "@faker-js/faker";
import type { WalletDto } from "@umi/api/schemas/wallet";
import type { Chain, ChainArch } from "@umi/shared/chains";
import { EvmManager } from "@umi/shared/managers/evm";
import { sha256 } from "js-sha256";
import _ from "lodash";


// Hash using sha256 the wallet address, name and tags.
// Used for syncing wallets with the server.
export const hashWallet = (wallet: {
  address: string;
  name: string;
  tags: string[];
  type: string;
}) => {
  const {
    address,
    name,
    tags,
    type,
  } = wallet;
  return sha256(JSON.stringify({
    address,
    name,
    tags: tags.join(""),
    type,
  }));
};


// Hash using sha256 the syncHash of all the wallets.
export const hashWalletsHashes = (wallets: { syncHash: WalletDto["syncHash"] }[]) => {
  return sha256(_.sortBy(wallets, ["addedAt"]).map(wallet => wallet.syncHash).join(""));
};


// Create a new private key for the given chain architecture.
export const createPk = (chainArch: Chain["arch"]) => {
  if (chainArch === "EVM") return EvmManager.createPk();
  throw new Error(`Cannot create private key for chain architecture: ${chainArch}`);
};


// Create a new wallet object based on a blueprint.
// This function is designed so it can be used both
// on the frontend and the backend.
export const createWallet = async <A extends ChainArch = ChainArch>(
  blueprint: {
    chainArch: A,
    pk?: string,
    name?: string,
    tags?: string[],
    nameAvoidList?: string[],
    addedAt?: Date,
    imported?: boolean,
  },
  encryptFn: (pk: string) => Promise<string>,
) => {

  const {
    name: explicitName,
    chainArch,
    pk = createPk(chainArch),
    tags = [],
    nameAvoidList = [],
    addedAt = new Date(),
    imported = false,
  } = blueprint;

  const name = explicitName ?? (() => {
    while (true) {
      // Prevent names that are in the avoid list
      const name = faker.person.lastName();
      if (!nameAvoidList.includes(name)) return name;
    }
  })();
  const encryptedPk = await encryptFn(pk);
  const address = (() => {
    if (chainArch === "EVM") return EvmManager.toAddress(pk);
    throw new Error(`Cannot derive wallet address for chain architecture: ${chainArch}`);
  })() as WalletDto["address"];

  return {
    name,
    encryptedPk,
    type: "internal",
    address,
    chainArch,
    tags,
    addedAt: addedAt.toISOString(),
    syncHash: hashWallet({
      address,
      name,
      tags,
      type: "internal"
    }),
    imported,
  } satisfies WalletDto;
};
